// Reads a Slippi replay (.slp) as its bytes arrive. Dolphin writes the replay
// while the game runs, so push() can be called with each new piece of the file;
// it returns the events completed by that piece and keeps any partial event for
// the next call. Only the events the live page needs are decoded: game start,
// post-frame updates and game end. Field offsets follow the replay spec,
// https://github.com/project-slippi/slippi-wiki/blob/master/SPEC.md
//
// A replay is a UBJSON object whose first key, "raw", holds the event stream:
//   {U\x03raw[$U#l <uint32 raw length> <events...> U\x08metadata{...}}
// The raw length is 0 until Dolphin finishes the file.

const HEADER = [0x7b, 0x55, 0x03, 0x72, 0x61, 0x77, 0x5b, 0x24, 0x55, 0x23, 0x6c];
const HEADER_LENGTH = HEADER.length + 4;
// The "U" that starts the metadata key after the raw stream. No event uses it.
const METADATA_START = 0x55;

const Command = {
	EVENT_PAYLOADS: 0x35,
	GAME_START: 0x36,
	POST_FRAME_UPDATE: 0x38,
	GAME_END: 0x39,
};

const PLAYER_TYPE_EMPTY = 3;

export default class SlpStreamParser {
	constructor() {
		this.buffer = new Uint8Array(0);
		this.headerRead = false;
		// Bytes of the raw stream still to read, or null while the length is 0.
		this.rawBytesLeft = null;
		this.payloadSizes = null;
		this.isComplete = false;
	}

	push(bytes) {
		if(this.isComplete) {
			return [];
		}
		this.buffer = concat(this.buffer, bytes);
		const events = [];
		if(!this.headerRead && !this.readHeader()) {
			return events;
		}
		let offset = 0;
		while(offset < this.buffer.length && (this.rawBytesLeft === null || offset < this.rawBytesLeft)) {
			const command = this.buffer[offset];
			// Dolphin writes the real raw length only when the game ends, after
			// this parser read it as 0, so the metadata marks the end instead.
			if(command === METADATA_START && this.rawBytesLeft === null) {
				this.isComplete = true;
				break;
			}
			const size = this.messageSize(command, offset);
			if(size === null || offset + size > this.buffer.length) {
				break;
			}
			const payload = this.buffer.subarray(offset, offset + size);
			if(command === Command.EVENT_PAYLOADS) {
				this.payloadSizes = readPayloadSizes(payload);
			}
			else {
				const event = parseEvent(command, payload);
				if(event) {
					events.push(event);
				}
			}
			offset += size;
		}
		if(this.rawBytesLeft !== null) {
			this.rawBytesLeft -= offset;
			this.isComplete = this.rawBytesLeft === 0;
		}
		this.buffer = this.buffer.slice(offset);
		return events;
	}

	readHeader() {
		if(this.buffer.length < HEADER_LENGTH) {
			const prefix = this.buffer.subarray(0, Math.min(this.buffer.length, HEADER.length));
			if(!prefix.every((byte, index) => byte === HEADER[index])) {
				throw new Error('This file is not a Slippi replay.');
			}
			return false;
		}
		if(!HEADER.every((byte, index) => this.buffer[index] === byte)) {
			throw new Error('This file is not a Slippi replay.');
		}
		const rawLength = new DataView(this.buffer.buffer, this.buffer.byteOffset).getUint32(HEADER.length);
		this.rawBytesLeft = rawLength === 0 ? null : rawLength;
		this.buffer = this.buffer.slice(HEADER_LENGTH);
		this.headerRead = true;
		return true;
	}

	// The whole message, command byte included, or null when it isn't known yet.
	messageSize(command, offset) {
		if(command === Command.EVENT_PAYLOADS) {
			if(offset + 1 >= this.buffer.length) {
				return null;
			}
			return this.buffer[offset + 1] + 1;
		}
		if(!this.payloadSizes) {
			throw new Error('The replay has no event payload sizes.');
		}
		const payloadSize = this.payloadSizes.get(command);
		if(payloadSize === undefined) {
			throw new Error('Unknown replay event 0x' + command.toString(16) + '.');
		}
		return payloadSize + 1;
	}
}

function concat(first, second) {
	if(first.length === 0) {
		return second.slice();
	}
	const joined = new Uint8Array(first.length + second.length);
	joined.set(first);
	joined.set(second, first.length);
	return joined;
}

function readPayloadSizes(payload) {
	const sizes = new Map();
	const view = viewOf(payload);
	for(let offset = 2; offset + 2 < payload.length; offset += 3) {
		sizes.set(payload[offset], view.getUint16(offset + 1));
	}
	return sizes;
}

function viewOf(payload) {
	return new DataView(payload.buffer, payload.byteOffset, payload.byteLength);
}

function parseEvent(command, payload) {
	switch(command) {
		case Command.GAME_START:
			return parseGameStart(payload);
		case Command.POST_FRAME_UPDATE:
			return parsePostFrame(payload);
		case Command.GAME_END:
			return parseGameEnd(payload);
		default:
			return null;
	}
}

// Older replays have shorter payloads; fields past the end read as null.
function reader(payload) {
	const view = viewOf(payload);
	const fits = (offset, size) => offset + size <= payload.length;
	return {
		uint8: offset => fits(offset, 1) ? view.getUint8(offset) : null,
		int8: offset => fits(offset, 1) ? view.getInt8(offset) : null,
		uint16: offset => fits(offset, 2) ? view.getUint16(offset) : null,
		int32: offset => fits(offset, 4) ? view.getInt32(offset) : null,
		uint32: offset => fits(offset, 4) ? view.getUint32(offset) : null,
		float: offset => fits(offset, 4) ? view.getFloat32(offset) : null,
		text: (offset, length, encoding) => fits(offset, length) ? decodeText(payload.subarray(offset, offset + length), encoding) : '',
	};
}

function decodeText(bytes, encoding) {
	const end = bytes.indexOf(0);
	const text = new TextDecoder(encoding).decode(end === -1 ? bytes : bytes.subarray(0, end));
	return toHalfwidth(text);
}

// Slippi stores names and connect codes in full-width characters.
function toHalfwidth(text) {
	return text.replace(/[！-～]/g, character => String.fromCharCode(character.charCodeAt(0) - 0xfee0)).replace(/　/g, ' ');
}

function parseGameStart(payload) {
	const read = reader(payload);
	const players = [0, 1, 2, 3].map(playerIndex => {
		const offset = playerIndex * 0x24;
		return {
			playerIndex,
			port: playerIndex + 1,
			characterId: read.uint8(0x65 + offset),
			type: read.uint8(0x66 + offset),
			startStocks: read.uint8(0x67 + offset),
			characterColor: read.uint8(0x68 + offset),
			teamId: read.uint8(0x6e + offset),
			nametag: read.text(0x161 + playerIndex * 0x10, 0x10, 'shift_jis'),
			displayName: read.text(0x1a5 + playerIndex * 0x1f, 0x1f, 'shift_jis'),
			connectCode: read.text(0x221 + playerIndex * 0xa, 0xa, 'shift_jis'),
		};
	}).filter(player => player.type !== PLAYER_TYPE_EMPTY);
	return {
		type: 'gameStart',
		slpVersion: [read.uint8(0x1), read.uint8(0x2), read.uint8(0x3)].join('.'),
		isTeams: read.uint8(0xd) === 1,
		stageId: read.uint16(0x13),
		players,
		isPAL: read.uint8(0x1a1) === 1,
		matchId: read.text(0x2be, 51, 'utf-8'),
		gameNumber: read.uint32(0x2f1),
		tiebreakerNumber: read.uint32(0x2f5),
	};
}

function parsePostFrame(payload) {
	const read = reader(payload);
	return {
		type: 'postFrame',
		frame: read.int32(0x1),
		playerIndex: read.uint8(0x5),
		isFollower: read.uint8(0x6) === 1,
		internalCharacterId: read.uint8(0x7),
		actionStateId: read.uint16(0x8),
		positionX: read.float(0xa),
		positionY: read.float(0xe),
		facingDirection: read.float(0x12),
		percent: read.float(0x16),
		stocksRemaining: read.uint8(0x21),
	};
}

function parseGameEnd(payload) {
	const read = reader(payload);
	return {
		type: 'gameEnd',
		gameEndMethod: read.uint8(0x1),
		lrasInitiatorIndex: read.int8(0x2),
		placements: [0, 1, 2, 3].map(playerIndex => read.int8(0x3 + playerIndex)),
	};
}
